if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Initialize zoom functionality
        this.initZoom();

        // Touch behavior: disable swipe scrolling and enable tap zones for prev/next
        try {
          this.isTouch = window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
        } catch (e) {
          this.isTouch = 'ontouchstart' in window;
        }

        if (this.isTouch && this.elements.viewer) {
          const viewerList = this.elements.viewer.querySelector('ul');
          if (viewerList) {
            viewerList.classList.add('no-touch-swipe');
            viewerList.setAttribute('data-no-swipe', 'true');
          }

          // Tap navigation on image area: left = previous, right = next
          this.onViewerTapBound = this.onViewerTap.bind(this);
          this.elements.viewer.addEventListener('click', this.onViewerTapBound);
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
          this.initThumbnailSlider();
        }
      }

      initZoom() {
        // Only enable zoom on non-touch (desktop/mouse/precision pointer)
        const isTouchEnv = (() => {
          try {
            return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
          } catch (e) {
            return 'ontouchstart' in window;
          }
        })();

        if (isTouchEnv) return;
        const mainImages = this.elements.viewer.querySelectorAll('.product__media-item img');
        mainImages.forEach(img => {
          // Add zoom cursor
          img.closest('.product__modal-opener--image').classList.add('image-magnify-hover');
          
          // Add click handler for zoom
          img.closest('.product__modal-opener--image').addEventListener('click', (e) => {
            const modalOpener = e.currentTarget;
            const imageWrapper = modalOpener.querySelector('.media');
            
            if (modalOpener.classList.contains('image-magnify-hover')) {
              // Switch to zoomed state
              modalOpener.classList.remove('image-magnify-hover');
              modalOpener.classList.add('image-magnify-full-size');
              imageWrapper.style.transform = 'scale(2)';
              imageWrapper.style.cursor = 'zoom-out';
            } else {
              // Switch back to normal state
              modalOpener.classList.add('image-magnify-hover');
              modalOpener.classList.remove('image-magnify-full-size');
              imageWrapper.style.transform = 'scale(1)';
              imageWrapper.style.cursor = 'zoom-in';
            }
          });
        });
      }

      // Handle tap navigation on touch devices
      onViewerTap(event) {
        // Only respond when tapping directly on an image opener (not videos/models/links)
        const opener = event.target.closest('.product__modal-opener--image');
        if (!opener) return;

        const rect = opener.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const leftHalf = x < rect.width / 2;

        event.preventDefault();
        event.stopPropagation();

        if (leftHalf) {
          this.goToPreviousMedia();
        } else {
          this.goToNextMedia();
        }
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
      }

      getActiveMediaIndex() {
        const items = this.getMediaItems();
        return Math.max(0, items.findIndex((el) => el.classList.contains('is-active')));
      }

      goToPreviousMedia() {
        const items = this.getMediaItems();
        if (!items.length) return;
        const currentIndex = this.getActiveMediaIndex();
        const prevIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
        const target = items[prevIndex];
        if (target && target.dataset.mediaId) {
          this.setActiveMedia(target.dataset.mediaId, false);
        }
      }

      goToNextMedia() {
        const items = this.getMediaItems();
        if (!items.length) return;
        const currentIndex = this.getActiveMediaIndex();
        const nextIndex = Math.min(items.length - 1, currentIndex + 1);
        const target = items[nextIndex];
        if (target && target.dataset.mediaId) {
          this.setActiveMedia(target.dataset.mediaId, false);
        }
      }

      initThumbnailSlider() {
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailList) return;

        const thumbnailItems = thumbnailList.querySelectorAll('.thumbnail-list__item');
        if (thumbnailItems.length <= 3) return;

        const prevButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');

        if (prevButton && nextButton) {
          const itemWidth = thumbnailItems[0].offsetWidth + 10; // Include gap
          const scrollAmount = itemWidth * 3;

          prevButton.addEventListener('click', () => {
            thumbnailList.scrollBy({
              left: -scrollAmount,
              behavior: 'smooth'
            });
          });

          nextButton.addEventListener('click', () => {
            thumbnailList.scrollBy({
              left: scrollAmount,
              behavior: 'smooth'
            });
          });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        // Remove active class from all media items
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        // Add active class to selected media
        activeMedia.classList.add('is-active');

        // Handle thumbnail active state
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        // Ensure the active media is visible (scroll disabled to prevent auto-scroll on variant change)
        // activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        // Reset zoom state for all images
        this.elements.viewer.querySelectorAll('.product__modal-opener--image').forEach(opener => {
          opener.classList.add('image-magnify-hover');
          opener.classList.remove('image-magnify-full-size');
          const media = opener.querySelector('.media');
          if (media) {
            media.style.transform = 'scale(1)';
            media.style.cursor = 'zoom-in';
          }
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Remove active state from all thumbnails
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
            element.parentElement.classList.remove('is-active');
          });

        // Add active state to selected thumbnail
        const button = thumbnail.querySelector('button');
        button.setAttribute('aria-current', true);
        thumbnail.classList.add('is-active');

        // Ensure the active thumbnail is visible in the slider (scroll disabled to prevent auto-scroll)
        // if (!this.isSlideVisible(thumbnail)) {
        //   thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        // }
      }

      isSlideVisible(element) {
        const container = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!container || !element) return false;

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        return (
          elementRect.left >= containerRect.left &&
          elementRect.right <= containerRect.right
        );
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings?.imageAvailable?.replace('[index]', position) || `Image ${position} available`;
        
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
